/* eslint-disable no-case-declarations */
import { Service } from '@genial/services';
import { MetaResponse } from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { ProductType } from '../../pages/ProductsManagement/constants';
import { blankMetaResponse } from '../../utils/blankMetaResponse';
import { customerFinancialQuery } from '../customer/selector';
import { CustomerFinancialResponseNormalized } from '../customer/types';

import {
  normalizeProductManagementAdvisorResponse,
  normalizeProductManagementDetailsResponse,
  normalizeProductManagementFixedIncomeProductsResponse,
  normalizeProductsManagementAvaliableToControlResponse,
} from './normalize';
import {
  ProductsManagementAdvisorResponse,
  ProductsManagementAvaliableItem,
  ProductsManagementDetailsResponse,
} from './types';

export const ProductsManagementAvaliableToControlQuery = selectorFamily<
  ProductsManagementAvaliableItem | undefined,
  {
    productType: number;
    filter?: string;
    hasFavorites?: boolean;
    blocked?: boolean;
    unblocked?: boolean;
  }
>({
  key: 'ProductManagement/ProductManagementList',
  get:
    ({ productType, filter = '', hasFavorites = false, blocked = false, unblocked = false }) =>
    async () => {
      const response =
        await Service.External.Extranet.Produtos.V1.produtosObterProdutosDisponiveisParaControle({
          screenName: 'ProductManagementList',
          tipoProduto: productType,
          filtro: filter,
          possuiFavoritos: hasFavorites,
          possuiBloqueios: blocked,
          naoPossuiBloqueios: unblocked,
        });

      const responseNormalized = normalizeProductsManagementAvaliableToControlResponse(response);

      return responseNormalized;
    },
});

export const ProductsManagementDetailQuery = selectorFamily<
  ProductsManagementDetailsResponse[] | undefined,
  {
    productId: number;
    productFilterIdAction: number;
    productTypeId?: number;
  }
>({
  key: 'ProductManagement/ProductManagementDetail',
  get:
    ({ productId, productFilterIdAction, productTypeId }) =>
    async () => {
      const vm = {
        idProdutoFiltroAcao: productFilterIdAction,
        idTipoProduto: productTypeId,
        ...(productTypeId === ProductType.FUNDS
          ? { idProduto: productId }
          : { idEmissorRendaFixa: productId }),
      };

      const response =
        await Service.External.Extranet.Produtos.V1.produtosObterControleProdutoDetalhado({
          vm,
          screenName: 'ProductManagementDetail',
        });

      const responseNormalized = normalizeProductManagementDetailsResponse(response);

      return responseNormalized;
    },
});

export const ProductsManagementSearchClientsOrAdvisorQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: ProductsManagementAdvisorResponse[];
    }
  | {
      meta: MetaResponse;
      data: CustomerFinancialResponseNormalized[];
    }
  | null
  | undefined,
  {
    searchTerm: string;
    searchType: string | undefined;
  }
>({
  key: 'ProductManagement/ProductManagementSearchClients',
  get:
    ({ searchTerm, searchType }) =>
    async ({ get }) => {
      if (!searchTerm || searchTerm.length < 3 || !searchType) {
        return { data: [], meta: blankMetaResponse() };
      }

      switch (searchType) {
        case 'advisor':
          const response =
            await Service.External.Extranet.Recurso.V1.recursoObterRecursosCadastradosPorFiltro({
              screenName: 'ProductManagementSearchClients',
              filtro: searchTerm,
            });

          const responseNormalized = normalizeProductManagementAdvisorResponse(response);

          return {
            data: responseNormalized,
            meta: blankMetaResponse(),
          };
        case 'client':
          return get(
            customerFinancialQuery({ searchTerm, page: 1, limit: 5, isAutocomplete: true })
          );
      }
    },
});

export const ProductsManagementFixedIncomeProductsQuery = selector({
  key: 'ProductsManagementFixedIncomeProductsQuery',
  get: async () => {
    const response = await Service.External.Extranet.RendaFixa.V1.rendaFixaObterProdutosRendaFixa({
      screenName: 'ProductsManagementBlockModal',
    });

    const normalizedData = normalizeProductManagementFixedIncomeProductsResponse(response);

    return normalizedData;
  },
});
